/**
 * DefaultLanding — where an authenticated user starts.
 *
 * The start page follows capabilities, since not everyone can load the same
 * surface: a platform admin in its own org holds only internal caps (no
 * monitoring.view). Today stays reachable from the nav; the default landing
 * never sends anyone there.
 *   - monitoring.view  -> /v4/records (Monitor conduct; firm users, and a
 *                                      platform admin acting as a client org)
 *   - GO Lab / legacy admin -> /v4/go-lab
 *   - ombudsman        -> /v3/ombudsman
 * Rendered as the index of the /v4 shell, so the nav frame is already up while
 * capabilities load.
 */
import React from 'react';
import { Navigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { useAccess, Capability } from '../../hooks/useAccess';
import { isAdmin, isOmbudsman } from '../../utils/rbac';

const DefaultLanding: React.FC = () => {
  const { user } = useAuth();
  const { can, isLoading } = useAccess();

  // Capabilities load async; the shell is already visible, so wait rather than
  // bounce to a page the user may not be able to load.
  if (isLoading) return null;

  if (can(Capability.MonitoringView)) return <Navigate to="/v4/records" replace />;
  if (isAdmin(user) || can(Capability.GolabAnnotate)) return <Navigate to="/v4/go-lab" replace />;
  if (isOmbudsman(user)) return <Navigate to="/v3/ombudsman" replace />;
  // Fallback: no recognised surface — lands on an empty Monitor conduct.
  return <Navigate to="/v4/records" replace />;
};

export default DefaultLanding;
